import React, { useState, useEffect } from "react"
import algoliasearch from "algoliasearch"
import {
  InstantSearch,
  Index,
  ExperimentalConfigureRelatedItems,
  Configure,
  connectHits,
  connectStateResults,
} from "react-instantsearch-dom"
import tw, { styled } from "twin.macro"
import { motion } from "framer-motion"
import { useStaticQuery, graphql } from "gatsby"

import InsightsCard from "components/insights-card"
import { filterHit, getUserToken } from "utils/algolia"

const facetsScores = {
  shows: 4,
  platforms: 3,
  markets: 2,
  genres: 1,
}

const facets = Object.keys(facetsScores)

const algoliaClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
)

const insightsIndex = algoliaClient.initIndex(
  process.env.GATSBY_ALGOLIA_INDEX_NAME
)

const buildOptionalWords = (algoliaObject) => {
  const { title } = algoliaObject
  return title.split(/\s+/)
}

const buildFacetsScores = (algoliaObject) => {
  return facets.reduce((prev, facet) => {
    if (algoliaObject[facet] && algoliaObject[facet].length > 0) {
      prev[facet] = { score: facetsScores[facet] }
    }
    return prev
  }, {})
}

const Main = styled.div`
  ${tw`max-w-s mx-auto m:max-w-m l:max-w-l`}
`

const Title = styled.h2`
  ${tw`py-4 pt-8 px-2 font-display text-black text-center l:text-left`}
  font-size: 28px;
`

const InsightsHits = connectHits(({ hits }) => (
  <>
    {hits.map((hit, idx) => {
      const post = filterHit(hit, {})
      return <InsightsCard post={post} key={idx} wide={true} />
    })}
  </>
))

const LoadingSpinner = ({ show }) => {
  if (!show) {
    return null
  }

  return (
    <div
      className="overflow-hidden w-full sticky"
      style={{
        top: 120,
        height: 3,
        backgroundColor: "rgb(27, 80, 115)",
        zIndex: 100,
      }}
    >
      <motion.div
        key="industry-selector-motion"
        animate={{ scaleX: 1, x: "100%" }}
        initial={{ scaleX: 0, x: 0 }}
        transition={{ duration: 2, repeatType: "loop", repeat: Infinity }}
        className="absolute"
        style={{
          backgroundColor: "#3498db",
          height: "3px",
          originX: 0,
          top: 0,
          width: "100%",
          zIndex: 100,
        }}
      ></motion.div>
    </div>
  )
}

const StateResults = connectStateResults(({ searchResults, setHitsCount }) => {
  const hitsCount = searchResults && searchResults.nbHits

  useEffect(() => {
    setHitsCount(hitsCount)
  }, [hitsCount])

  return null
})

const OtherInsights = ({ postId }) => {
  const [algoliaObject, setAlgoliaObject] = useState(null)
  const [hitsCount, setHitsCount] = useState(null)

  const { gcms: settings } = useStaticQuery(graphql`
    {
      gcms {
        insightsSearch: websiteSetting(where: { name: "Insights search" }) {
          jsonValue
        }
      }
    }
  `)

  const insightsConfig = settings.insightsSearch.jsonValue
  const fallbackResult = {
    title: insightsConfig.fallbackTitle,
    excerpt: insightsConfig.fallbackExcerpt,
    path: insightsConfig.fallbackLinkTo,
    featuredImage: { url: insightsConfig.fallbackImage },
    linkCaption: insightsConfig.fallbackLinkCaption,
  }

  useEffect(() => {
    async function loadAlgoliaObject() {
      const { results } = await insightsIndex.getObjects([postId], {
        attributesToRetrieve: ["objectID", "title", ...facets],
      })
      const [object] = results
      setAlgoliaObject(object)
    }

    loadAlgoliaObject()
  }, [])

  if (algoliaObject === null) {
    return null
  }

  return (
    <div className="pb-8 mt-16">
      <Main>
        <Title>Other insights you may enjoy</Title>
        <div className="mx-auto max-w-s m:max-w-m l:max-w-l">
          <div className="flex flex-wrap m:-mx-2 overflow-display">
            <LoadingSpinner
              show={algoliaObject === null || hitsCount === null}
            />
            <InstantSearch
              indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}
              searchClient={algoliaClient}
            >
              <StateResults setHitsCount={setHitsCount} />
              <div className="mx-auto max-w-s m:max-w-m l:max-w-l">
                <div className="flex flex-wrap m:-mx-2 overflow-display">
                  <Index indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}>
                    <Configure
                      distinct
                      hitsPerPage={3}
                      analytics={false}
                      enablePersonalization={true}
                      userToken={getUserToken()}
                    />
                    <ExperimentalConfigureRelatedItems
                      hit={algoliaObject}
                      matchingPatterns={buildFacetsScores(algoliaObject)}
                      transformSearchParameters={(searchParameters) => {
                        return {
                          ...searchParameters,
                          optionalWords: buildOptionalWords(algoliaObject),
                        }
                      }}
                    />
                    <InsightsHits />
                  </Index>
                  {hitsCount === 0 && (
                    <>
                      <InsightsCard
                        post={fallbackResult}
                        key="insights-fallback"
                        wide={true}
                      />
                    </>
                  )}
                </div>
              </div>
            </InstantSearch>
          </div>
        </div>
      </Main>
    </div>
  )
}

export default OtherInsights
